import { useRef, useState } from 'react';

/**
 * Keyed image-tile editor — a list of `{ label, image }` rows where each label carries one
 * uploaded image. Shared by the storefront Brand Assets panel (collection/occasion tiles) and
 * the Product Metadata → Occasions tab. Pure presentation: the parent owns upload + persistence
 * and passes `onUpload`/`onRemove`.
 */
export interface KeyedImageSlotProps {
  title: string;
  hint: string;
  placeholder: string;
  rows: { label: string; image: string }[];
  busyLabel: string | null;
  onUpload: (label: string, file: File | undefined) => void;
  onRemove: (label: string) => void;
}

export function KeyedImageSlot({ title, hint, placeholder, rows, busyLabel, onUpload, onRemove }: KeyedImageSlotProps) {
  const [newLabel, setNewLabel] = useState('');
  const addInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replaceLabel, setReplaceLabel] = useState<string | null>(null);

  return (
    <div className="border border-border rounded-[10px] p-3 mt-4">
      <div className="mb-2">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
      </div>

      {/* Existing rows */}
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-[6px] overflow-hidden flex-shrink-0 border border-border bg-muted">
              <img src={row.image} alt={row.label} className="w-full h-full object-cover" />
            </div>
            <span className="text-[12px] font-medium text-foreground flex-1 min-w-0 truncate">{row.label}</span>
            <button
              onClick={() => {
                setReplaceLabel(row.label);
                replaceInputRef.current?.click();
              }}
              disabled={busyLabel === row.label}
              className="px-2 py-1 border border-border rounded-md text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
            >
              {busyLabel === row.label ? 'Uploading…' : 'Replace'}
            </button>
            <button
              onClick={() => onRemove(row.label)}
              className="text-[11px] text-muted-foreground hover:text-red-500"
              title="Remove"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {/* Hidden input reused for replacing an existing row */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (replaceLabel) onUpload(replaceLabel, e.target.files?.[0]);
          setReplaceLabel(null);
          e.target.value = '';
        }}
      />

      {/* Add a new label + image */}
      <div className="flex items-center gap-2 mt-3">
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={placeholder}
          className="flex-1 min-w-0 px-2.5 py-1.5 border border-border rounded-md text-[12px] bg-background text-foreground"
        />
        <input
          ref={addInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            onUpload(newLabel, e.target.files?.[0]);
            setNewLabel('');
            e.target.value = '';
          }}
        />
        <button
          onClick={() => addInputRef.current?.click()}
          disabled={!newLabel.trim() || busyLabel === newLabel.trim()}
          className="px-2.5 py-1.5 border border-border rounded-md text-[11px] font-medium text-foreground hover:bg-muted disabled:opacity-50 flex-shrink-0"
        >
          {busyLabel === newLabel.trim() ? 'Uploading…' : 'Upload image'}
        </button>
      </div>
    </div>
  );
}
